import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
} from "@angular/core";
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
  MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { getFileExtLabel, getFileBadgeColor } from "./utils/helpers.utils";
import { downloadFile } from "../helpers/helper";
import { fetchDocument, isPrivateDocument } from "../helpers/private-document";
import { loadPdfJs } from "../helpers/pdfjs-loader";

export type DocumentPreviewFile = {
  fileUrl: string;
  name?: string;
  /** Pre-formatted size label shown under the name, e.g. "1.2 MB" */
  sizeLabel?: string;
};

type PreviewMode = "pdf" | "docx" | "sheet" | "text" | "office" | "unsupported";

const PDF_EXTS = ["pdf"];
const DOCX_EXTS = ["docx"];
const SHEET_EXTS = ["xlsx", "xls", "xlsm", "ods", "csv", "tsv"];
/** Formats with no in-browser renderer; shown through Office Online when the file is publicly reachable. */
const OFFICE_EXTS = ["doc", "ppt", "pptx", "pps", "ppsx", "odt", "odp", "rtf"];
const TEXT_EXTS = [
  "txt", "log", "md", "json", "xml", "yaml", "yml", "ini", "toml", "env", "sql",
  "js", "ts", "jsx", "tsx", "py", "java", "html", "htm", "css", "scss", "sass", "less",
  "c", "cpp", "h", "hpp", "cs", "php", "rb", "go", "rs", "swift", "kt", "kts", "sh", "bash",
  "dart", "vue", "gitignore",
];

/** Above this size the file is not parsed in the browser; the user is offered a download instead. */
const MAX_INLINE_BYTES = 30 * 1024 * 1024;
const MAX_TEXT_CHARS = 500_000;
const MAX_PDF_PAGES = 100;
const MAX_SHEET_ROWS = 1000;
const MAX_SHEET_COLS = 100;

/** Opens the in-app document preview, matching the image viewer's full-screen dialog. */
export function openDocumentPreview(dialog: MatDialog, file: DocumentPreviewFile) {
  if (!file?.fileUrl) return;
  return dialog.open(DocumentPreviewDialogComponent, {
    data: file,
    panelClass: "image-viewer-dialog-panel",
    backdropClass: "image-viewer-dialog-backdrop",
    height: "100vh",
    width: "100vw",
    maxHeight: "100vh",
    maxWidth: "100vw",
    autoFocus: false,
  });
}

@Component({
  selector: "app-document-preview-dialog",
  standalone: true,
  imports: [MatDialogModule],
  template: `
<div class="doc-dialog">
  <div class="viewer-header">
    <div class="header-left">
      <button class="back-btn-dialog" (click)="dialogRef.close()" title="Close preview">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
      </button>
      <svg class="header-badge" width="30" height="33" viewBox="0 0 42 46" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6 0C2.686 0 0 2.686 0 6V40C0 43.314 2.686 46 6 46H36C39.314 46 42 43.314 42 40V12L30 0H6Z" [attr.fill]="badgeColor" />
        <path d="M30 0V8C30 10.209 31.791 12 34 12H42L30 0Z" fill="rgba(255, 255, 255, 0.3)" />
        <text x="20" y="31" font-size="15" font-weight="500" fill="#ffffff" text-anchor="middle" letter-spacing="0.5px">{{ extLabel }}</text>
      </svg>
      <div class="file-meta">
        <span class="file-title" [title]="fileName">{{ fileName }}</span>
        @if (data.sizeLabel) {
          <span class="file-size">{{ data.sizeLabel }}</span>
        }
      </div>
    </div>

    <div class="dialog-actions">
      <button class="action-btn download-btn" (click)="download()" [disabled]="downloading" title="Download file">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
        <span class="btn-label">{{ downloading ? 'Downloading…' : 'Download' }}</span>
      </button>
      <button class="action-btn close-btn" (click)="dialogRef.close()" title="Close">✕</button>
    </div>
  </div>

  <div class="doc-stage">
    @if (loading) {
      <div class="state-box">
        <div class="spinner"></div>
        <p>Loading preview…</p>
      </div>
    }

    @if (mode === 'unsupported' && !loading) {
      <div class="state-box fallback">
        <svg width="84" height="92" viewBox="0 0 42 46" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 0C2.686 0 0 2.686 0 6V40C0 43.314 2.686 46 6 46H36C39.314 46 42 43.314 42 40V12L30 0H6Z" [attr.fill]="badgeColor" />
          <path d="M30 0V8C30 10.209 31.791 12 34 12H42L30 0Z" fill="rgba(255, 255, 255, 0.3)" />
          <text x="20" y="31" font-size="15" font-weight="500" fill="#ffffff" text-anchor="middle" letter-spacing="0.5px">{{ extLabel }}</text>
        </svg>
        <p class="fallback-title">{{ fallbackTitle }}</p>
        <p class="fallback-text">{{ fallbackText }}</p>
        <button class="primary-download" (click)="download()" [disabled]="downloading">
          {{ downloading ? 'Downloading…' : 'Download file' }}
        </button>
      </div>
    }

    @if (mode === 'pdf') {
      <div class="paper-scroll" #pdfContainer [class.hidden]="loading"></div>
    }

    @if (mode === 'docx' && !loading) {
      <div class="paper-scroll">
        <div class="paper docx-content" [innerHTML]="htmlContent"></div>
      </div>
    }

    @if (mode === 'sheet' && !loading) {
      @if (sheetNames.length > 1) {
        <div class="sheet-tabs">
          @for (name of sheetNames; track name; let i = $index) {
            <button class="sheet-tab" [class.active]="i === activeSheet" (click)="selectSheet(i)">{{ name }}</button>
          }
        </div>
      }
      <div class="paper-scroll">
        <div class="paper sheet-content" [innerHTML]="htmlContent"></div>
      </div>
    }

    @if (mode === 'text' && !loading) {
      <div class="paper-scroll">
        <pre class="paper text-content">{{ textContent }}</pre>
      </div>
    }

    @if (mode === 'office' && embedUrl) {
      <iframe class="embed-frame" [src]="embedUrl" (load)="onEmbedLoaded()" title="Document preview"></iframe>
    }
  </div>

  @if (notice && !loading && mode !== 'unsupported') {
    <div class="dialog-footer">
      <span>{{ notice }}</span>
      <button class="link-btn" (click)="download()">Download</button>
    </div>
  }
</div>
`,
  styles: [
    `
      :host {
        display: block;
        width: 100vw;
        height: 100vh;
        max-width: 100vw !important;
        max-height: 100vh !important;
        overflow: hidden;
        --doc-stage-bg: #e9ebef;
        --doc-paper-bg: #ffffff;
        --doc-paper-text: #1f2937;
        --doc-border: #d6d9df;
        --doc-muted: #6b7280;
        --doc-tab-bg: #ffffff;
        --doc-accent: #4e85e2;
      }

      :host-context(.dark-theme) {
        --doc-stage-bg: var(--bg-app-solid, #121212);
        --doc-paper-bg: var(--bg-card, #1e1e1e);
        --doc-paper-text: var(--text-primary, #e5e7eb);
        --doc-border: var(--border-color, #333);
        --doc-muted: var(--text-muted, #9ca3af);
        --doc-tab-bg: var(--bg-hover, #2a2a2a);
        --doc-accent: #93c5fd;
      }

      .doc-dialog {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        background: var(--doc-stage-bg);
      }

      .viewer-header {
        height: 64px;
        flex-shrink: 0;
        background: rgba(0, 0, 0, 0.85);
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        padding: 0 20px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      }

      .header-left {
        display: flex;
        align-items: center;
        gap: 12px;
        min-width: 0;
      }

      .back-btn-dialog {
        background: transparent;
        border: none;
        color: white;
        cursor: pointer;
        padding: 8px;
        border-radius: 50%;
        display: flex;
        transition: background 0.2s;
      }

      .back-btn-dialog:hover { background: rgba(255, 255, 255, 0.1); }

      .header-badge { flex-shrink: 0; }

      .file-meta {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }

      .file-title {
        color: #fff;
        font-weight: 500;
        font-size: 1rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .file-size {
        color: rgba(255, 255, 255, 0.6);
        font-size: 0.8rem;
      }

      .dialog-actions {
        display: flex;
        gap: 12px;
        flex-shrink: 0;
      }

      .action-btn {
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid rgba(255, 255, 255, 0.1);
        color: #fff;
        height: 40px;
        min-width: 40px;
        padding: 0 14px;
        border-radius: 20px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        font-size: 14px;
        transition: all 0.2s ease;
      }

      .action-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.18); }
      .action-btn:disabled { opacity: 0.6; cursor: default; }
      .close-btn { padding: 0; width: 40px; font-size: 18px; }

      .doc-stage {
        position: relative;
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
      }

      .paper-scroll {
        flex: 1;
        min-height: 0;
        overflow: auto;
        padding: 24px 16px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
      }

      .paper-scroll.hidden { visibility: hidden; }

      .paper {
        background: var(--doc-paper-bg);
        color: var(--doc-paper-text);
        border-radius: 4px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        width: 100%;
        max-width: 900px;
        box-sizing: border-box;
      }

      :host ::ng-deep .pdf-page {
        width: 100%;
        max-width: 900px;
        height: auto;
        background: #fff;
        border-radius: 2px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.18);
      }

      .docx-content {
        padding: 48px 56px;
        line-height: 1.6;
        word-wrap: break-word;
      }

      :host ::ng-deep .docx-content img { max-width: 100%; height: auto; }
      :host ::ng-deep .docx-content table { border-collapse: collapse; margin: 12px 0; }
      :host ::ng-deep .docx-content td,
      :host ::ng-deep .docx-content th { border: 1px solid var(--doc-border); padding: 4px 8px; }

      .sheet-content {
        max-width: none;
        width: auto;
        min-width: 100%;
        padding: 0;
        overflow: visible;
        align-self: flex-start;
      }

      :host ::ng-deep .sheet-content table {
        border-collapse: collapse;
        font-size: 13px;
      }

      :host ::ng-deep .sheet-content td,
      :host ::ng-deep .sheet-content th {
        border: 1px solid var(--doc-border);
        padding: 4px 10px;
        white-space: nowrap;
        max-width: 360px;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .sheet-tabs {
        display: flex;
        gap: 6px;
        padding: 10px 16px 0;
        overflow-x: auto;
        flex-shrink: 0;
      }

      .sheet-tab {
        border: 1px solid var(--doc-border);
        background: var(--doc-tab-bg);
        color: var(--doc-paper-text);
        padding: 6px 14px;
        border-radius: 6px 6px 0 0;
        cursor: pointer;
        white-space: nowrap;
        font-size: 13px;
      }

      .sheet-tab.active {
        border-bottom-color: var(--doc-accent);
        box-shadow: inset 0 -2px 0 var(--doc-accent);
        font-weight: 600;
      }

      .text-content {
        margin: 0;
        padding: 20px 24px;
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        font-size: 13px;
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-word;
      }

      .embed-frame {
        flex: 1;
        width: 100%;
        border: none;
        background: #fff;
      }

      .state-box {
        position: absolute;
        inset: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
        color: var(--doc-muted);
        padding: 24px;
        text-align: center;
      }

      .fallback-title {
        margin: 8px 0 0;
        font-size: 1.1rem;
        font-weight: 600;
        color: var(--doc-paper-text);
      }

      .fallback-text {
        margin: 0;
        max-width: 420px;
        font-size: 0.9rem;
      }

      .primary-download {
        margin-top: 8px;
        padding: 10px 24px;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        font-weight: 500;
        color: #fff;
        cursor: pointer;
        background: linear-gradient(135deg, #6565AC, #86A8E7);
        transition: all 0.2s ease;
      }

      .primary-download:hover:not(:disabled) {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(101, 101, 172, 0.4);
      }

      .primary-download:disabled { opacity: 0.6; cursor: default; }

      .spinner {
        width: 36px;
        height: 36px;
        border: 3px solid var(--doc-border);
        border-top-color: var(--doc-accent);
        border-radius: 50%;
        animation: doc-spin 0.8s linear infinite;
      }

      @keyframes doc-spin { to { transform: rotate(360deg); } }

      .dialog-footer {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        padding: 10px 16px;
        font-size: 13px;
        color: var(--doc-muted);
        border-top: 1px solid var(--doc-border);
        text-align: center;
      }

      .link-btn {
        background: none;
        border: none;
        padding: 0;
        color: var(--doc-accent);
        font-weight: 600;
        cursor: pointer;
      }

      @media (max-width: 580px) {
        .viewer-header { padding: 0 10px; gap: 8px; }
        .header-left { gap: 8px; }
        .header-badge { display: none; }
        .back-btn-dialog { padding: 5px; }
        .btn-label { display: none; }
        .action-btn { padding: 0; width: 38px; height: 38px; }
        .dialog-actions { gap: 8px; }
        .file-title { font-size: 14px; }
        .paper-scroll { padding: 12px 8px; gap: 10px; }
        .docx-content { padding: 20px 16px; }
        .text-content { padding: 14px; font-size: 12px; }
      }
    `,
  ],
})
export class DocumentPreviewDialogComponent implements OnInit, OnDestroy {
  mode: PreviewMode = "unsupported";
  loading = true;
  downloading = false;

  fileName = "";
  extLabel = "";
  badgeColor = "";

  htmlContent = "";
  textContent = "";
  embedUrl: SafeResourceUrl | null = null;
  notice = "";
  fallbackTitle = "Preview not available";
  fallbackText = "This file type can't be previewed here. Download it to open it on your device.";

  sheetNames: string[] = [];
  activeSheet = 0;

  private workbook: any = null;
  private xlsx: any = null;
  private pdfTask: any = null;
  private destroyed = false;

  @ViewChild("pdfContainer") pdfContainer?: ElementRef<HTMLDivElement>;

  constructor(
    public dialogRef: MatDialogRef<DocumentPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentPreviewFile,
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const url = this.data?.fileUrl || "";
    this.fileName = this.data?.name || decodeURIComponent(url.split(/[?#]/)[0].split("/").pop() || "File");
    this.extLabel = getFileExtLabel(this.fileName || url);
    this.badgeColor = getFileBadgeColor(this.fileName || url);
    void this.loadPreview();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.pdfTask) void this.pdfTask.destroy();
  }

  async download(): Promise<void> {
    if (this.downloading) return;
    this.downloading = true;
    try {
      await downloadFile(this.data.fileUrl, this.fileName);
    } finally {
      this.downloading = false;
      this.cdRef.markForCheck();
    }
  }

  selectSheet(index: number): void {
    if (!this.workbook || index === this.activeSheet) return;
    this.activeSheet = index;
    this.renderSheet();
  }

  onEmbedLoaded(): void {
    this.loading = false;
    this.cdRef.markForCheck();
  }

  private get extension(): string {
    const source = (this.fileName || this.data.fileUrl || "").split(/[?#]/)[0].toLowerCase();
    const dot = source.lastIndexOf(".");
    return dot >= 0 ? source.substring(dot + 1) : "";
  }

  private async loadPreview(): Promise<void> {
    const ext = this.extension;
    try {
      if (PDF_EXTS.includes(ext)) {
        this.mode = "pdf";
        await this.renderPdf();
      } else if (DOCX_EXTS.includes(ext)) {
        this.mode = "docx";
        await this.renderDocx();
      } else if (SHEET_EXTS.includes(ext)) {
        this.mode = "sheet";
        await this.loadSheet(ext);
      } else if (TEXT_EXTS.includes(ext)) {
        this.mode = "text";
        await this.renderText();
      } else if (OFFICE_EXTS.includes(ext)) {
        this.showOfficeEmbed();
        return;
      } else {
        this.showFallback();
      }
    } catch (e) {
      if (this.destroyed) return;
      console.warn("Document preview failed:", (e as any)?.message || e);
      // Formats Office Online can render get a second chance there when the file could not be read in the page.
      if ((DOCX_EXTS.includes(ext) || SHEET_EXTS.includes(ext)) && !["csv", "tsv"].includes(ext)) {
        this.showOfficeEmbed();
        return;
      }
      this.showFallback(
        "Couldn't load preview",
        "Something went wrong while opening this file. You can still download it."
      );
    }
    this.finishLoading();
  }

  private finishLoading(): void {
    if (this.destroyed) return;
    this.loading = false;
    this.cdRef.markForCheck();
  }

  private showFallback(title?: string, text?: string): void {
    this.mode = "unsupported";
    if (title) this.fallbackTitle = title;
    if (text) this.fallbackText = text;
    this.finishLoading();
  }

  /** Office Online needs to fetch the file itself, so it only works for publicly reachable links. */
  private showOfficeEmbed(): void {
    const url = this.data.fileUrl;
    if (!/^https?:\/\//i.test(url) || isPrivateDocument(url)) {
      this.showFallback();
      return;
    }
    this.mode = "office";
    this.embedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`
    );
    this.notice = "Previewed with Office Online. If it doesn't load,";
    this.cdRef.markForCheck();
  }

  private async fetchBlob(): Promise<Blob> {
    const res = await fetchDocument(this.data.fileUrl);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const length = Number(res.headers.get("content-length") || 0);
    if (length > MAX_INLINE_BYTES) throw new TooLargeError();
    const blob = await res.blob();
    if (blob.size > MAX_INLINE_BYTES) throw new TooLargeError();
    return blob;
  }

  private async renderPdf(): Promise<void> {
    let blob: Blob;
    try {
      blob = await this.fetchBlob();
    } catch (e) {
      if (e instanceof TooLargeError) return this.showTooLarge();
      throw e;
    }
    const pdfjs = await loadPdfJs();
    if (this.destroyed) return;
    // The page container only exists once the 'pdf' branch of the template has rendered.
    this.cdRef.detectChanges();
    this.pdfTask = pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()) });
    const doc = await this.pdfTask.promise;
    const pageCount = Math.min(doc.numPages, MAX_PDF_PAGES);
    if (doc.numPages > MAX_PDF_PAGES) {
      this.notice = `Showing the first ${MAX_PDF_PAGES} of ${doc.numPages} pages.`;
    }

    // Show the dialog as soon as the first page is drawn; the rest stream in below it.
    for (let n = 1; n <= pageCount; n++) {
      if (this.destroyed) return;
      const container = this.pdfContainer?.nativeElement;
      if (!container) return;
      const page = await doc.getPage(n);
      const base = page.getViewport({ scale: 1 });
      const cssWidth = Math.min(container.clientWidth - 32 || base.width, 900);
      const scale = (cssWidth / base.width) * Math.min(window.devicePixelRatio || 1, 2);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.className = "pdf-page";
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      container.appendChild(canvas);
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;
      await page.render({ canvasContext: ctx, canvas, viewport }).promise;
      if (n === 1) this.finishLoading();
    }
  }

  private async renderDocx(): Promise<void> {
    let blob: Blob;
    try {
      blob = await this.fetchBlob();
    } catch (e) {
      if (e instanceof TooLargeError) return this.showTooLarge();
      throw e;
    }
    const mammoth: any = await import("mammoth");
    const lib = mammoth.default || mammoth;
    const result = await lib.convertToHtml({ arrayBuffer: await blob.arrayBuffer() });
    if (!result?.value?.trim()) throw new Error("Empty document");
    // Bound through [innerHTML], so Angular sanitizes the converted markup.
    this.htmlContent = result.value;
  }

  private async loadSheet(ext: string): Promise<void> {
    let blob: Blob;
    try {
      blob = await this.fetchBlob();
    } catch (e) {
      if (e instanceof TooLargeError) return this.showTooLarge();
      throw e;
    }
    this.xlsx = await import("xlsx");
    if (ext === "csv" || ext === "tsv") {
      const text = await blob.text();
      this.workbook = this.xlsx.read(text, { type: "string", FS: ext === "tsv" ? "\t" : undefined });
    } else {
      this.workbook = this.xlsx.read(new Uint8Array(await blob.arrayBuffer()), { type: "array" });
    }
    this.sheetNames = this.workbook.SheetNames || [];
    if (!this.sheetNames.length) throw new Error("No sheets");
    this.activeSheet = 0;
    this.renderSheet();
  }

  private renderSheet(): void {
    const XLSX = this.xlsx;
    const sheet = this.workbook.Sheets[this.sheetNames[this.activeSheet]];
    this.notice = "";
    if (!sheet || !sheet["!ref"]) {
      this.htmlContent = `<p style="padding:16px">This sheet is empty.</p>`;
      return;
    }
    const range = XLSX.utils.decode_range(sheet["!ref"]);
    const rows = range.e.r - range.s.r + 1;
    const cols = range.e.c - range.s.c + 1;
    const view = { ...sheet };
    if (rows > MAX_SHEET_ROWS || cols > MAX_SHEET_COLS) {
      view["!ref"] = XLSX.utils.encode_range({
        s: range.s,
        e: {
          r: Math.min(range.e.r, range.s.r + MAX_SHEET_ROWS - 1),
          c: Math.min(range.e.c, range.s.c + MAX_SHEET_COLS - 1),
        },
      });
      this.notice = `Showing the first ${Math.min(rows, MAX_SHEET_ROWS)} rows and ${Math.min(cols, MAX_SHEET_COLS)} columns.`;
    }
    this.htmlContent = XLSX.utils.sheet_to_html(view, { header: "", footer: "" });
    this.cdRef.markForCheck();
  }

  private async renderText(): Promise<void> {
    let blob: Blob;
    try {
      blob = await this.fetchBlob();
    } catch (e) {
      if (e instanceof TooLargeError) return this.showTooLarge();
      throw e;
    }
    const text = await blob.text();
    if (text.length > MAX_TEXT_CHARS) {
      this.textContent = text.substring(0, MAX_TEXT_CHARS);
      this.notice = "This file is long; only the beginning is shown.";
    } else {
      this.textContent = text;
    }
  }

  private showTooLarge(): void {
    this.showFallback("File too large to preview", "This file is too large to preview here. Download it to view the full file.");
  }
}

class TooLargeError extends Error {}
